import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import moment from 'moment-timezone';
import ReactEcharts from "echarts-for-react";
import AnalyticChartDateOptions from './AnalyticChartDateOptions';
import LoaderGraph from "../general/LoaderGraph";

import { useSite } from "../../util/sites";
import GYAxios from "../../util/GYAxios";
import { useFormatter } from "../../util/formatter";
import { useSolarCalc } from '../../util/calculations';
import usePeakLabels from "../../util/peakLabels";

// util functions moved to here, to prevent infinite rendering.
const [formatNumber] = useFormatter();
const { parseFormatNumber } = useSolarCalc();
const solarCalc = useSolarCalc();

export default function AnalyticAllDataGraph({ toggleCharts, selectedChart, setSelectedChart, ...props }) {
	const currentSite = useSite();
	const selectedSite = currentSite.selectedSite;
	const currentMonth = moment().format("MMM YYYY");
	const showDEO = selectedSite.value.country === 'VN' || selectedSite.value.country === 'TH';
	let minDateStr = selectedSite.value.msa || selectedSite.value.cod || moment().add(1, 'year').toISOString(); // limit to MSA or COD date
	const minDate = useMemo(() => moment(minDateStr).startOf('date'), [minDateStr]);

	const solarLabel = usePeakLabels(currentSite.selectedSite.value.country);

	const [sensorDataRes, setSensorDataRes] = useState([]);
	const [sensorDataGraphConfig, setSensorDataGraphConfig] = useState(null);
	const [sensorData, setSensorData] = useState(null);
	const defaultSensorDataFilter = useMemo(() => ({ interval: '1M', range: '6M', chart: selectedChart, moments: [moment().subtract(5, 'month').startOf('month'), moment().startOf('month')] }), [selectedChart]);
	const [sensorDataFilter, setSensorDataFilter] = useState(defaultSensorDataFilter);
	const prevSensorDataRes = useRef(null);
	const sensorDataIntervalFilterOptions = useMemo(() => ([
		{ label: 'Monthly', value: '1M', timeSpan: 1000 * 60 * 60 * 24 * 30, visibleRangeOptions: ['3M', '6M', '12M', '1Y', 'PAST_1Y'] },
		{ label: 'Yearly', value: '1Y', timeSpan: 1000 * 60 * 60 * 24 * 365, visibleRangeOptions: ['CALENDAR_ALL', 'CONTRACT_ALL'] },
	]), []);
	const sensorDataRangeFilterOptions = useMemo(() => ([
		{ label: 'Last 3 months', value: '3M', moments: [moment().subtract(2, 'month').startOf('month'), moment().startOf('day')] },
		{ label: 'Last 6 months', value: '6M', moments: [moment().subtract(5, 'month').startOf('month'), moment().startOf('day')] },
		{ label: 'Last 12 months', value: '12M', moments: [moment().subtract(11, 'month').startOf('month'), moment().startOf('day')] },
		{ label: 'This year', value: '1Y', moments: [moment().startOf('year'), moment().endOf('year').startOf('day')] },
		{ label: 'Last year', value: 'PAST_1Y', moments: [moment().subtract(1, 'year').startOf('year'), moment().subtract(1, 'year').endOf('year').startOf('day')] },
		{ label: 'All calendar years', value: 'CALENDAR_ALL', moments: [moment(minDate), moment().startOf('day')] },
		{ label: 'All contract years', value: 'CONTRACT_ALL', moments: [moment(minDate), moment().startOf('day')] },
	]), [minDate]);
	const dateRangeOtherOptions = useMemo(() => ([
		{
			label: 'Chart',
			name: 'chart',
			options: [
				{ label: 'Production', value: toggleCharts.productionChart },
				{ label: 'All Data', value: toggleCharts.sensorChart },
			]
		}
	]), [toggleCharts]);
	const dateRangeControlledOtherVal = useMemo(() => ({ chart: selectedChart }), [selectedChart]);
	const showHelperText = useMemo(() => sensorDataFilter?.range === 'CONTRACT_ALL', [sensorDataFilter]);
	const helperText = useMemo(() => {
		return (
			<div>
				<div>Contract year start on {selectedSite.value.msa ? 'MSA' : 'COD'} date in the contract.</div>
				<div>The first contract year is {minDate.format('MMM D, YYYY')} - {moment(minDate).add(1, 'year').subtract(1, 'day').format('MMM D, YYYY')},</div>
				<div>the second contract year is {moment(minDate).add(1, 'year').format('MMM D, YYYY')} - {moment(minDate).add(2, 'year').subtract(1, 'day').format('MMM D, YYYY')},</div>
				<div>and so on.</div>
			</div>
		);
	}, [minDate, selectedSite]);

	const handleSensorDataFilterChange = useCallback(val => {
		setSensorDataFilter(val);
	}, []);

	const handleOtherOptionsChange = useCallback(val => {
		if ('chart' in val) {
			setSelectedChart(val.chart);
		}
	}, [setSelectedChart]);

	// fetch sensor data
	const getSensorData = useCallback(() => {
		if (sensorDataFilter.interval !== '1M' && sensorDataFilter.interval !== '1Y') { return }
		if (!sensorDataFilter.moments?.length || typeof (selectedSite?.value?.site_id) !== 'number') { return }

		/** @type {string} */
		let interval;
		/** @type {string} */
		let orderBy;
		/** @type {string} */
		let API;
		/** @type {string} */
		let fromDate, toDate;
		/** @type {number} */
		const siteId = selectedSite.value.site_id;
		const clientId = selectedSite.value.client_id;

		fromDate = sensorDataFilter.moments[0];
		toDate = sensorDataFilter.moments[1];

		// fromDate must not be before MSA/COD date
		if (fromDate < minDate) { fromDate = minDate }
		if (toDate < fromDate) { toDate = moment(fromDate) }

		if (sensorDataFilter.interval === '1M') {
			interval = 'monthly';
			orderBy = 'dateData';
			fromDate = fromDate.format('YYYY-MM-DD');
			toDate = toDate.format('YYYY-MM-DD');
			API = `/${interval}/site/${siteId}/period/${fromDate}/${toDate}/order/${orderBy}/ASC`;
		}
		if (sensorDataFilter.interval === '1Y' && (sensorDataFilter.range === 'CALENDAR_ALL' || sensorDataFilter.range.type === 'DATE_RANGE_PICKER')) {
			interval = 'yearly';
			orderBy = 'yearData';
			fromDate = fromDate.format('YYYY-MM-DD');
			toDate = toDate.format('YYYY-MM-DD');
			API = `/${interval}/client/${clientId}/site/${siteId}/period/${fromDate}/${toDate}/order/${orderBy}/ASC`;
		}
		if (sensorDataFilter.interval === '1Y' && sensorDataFilter.range === 'CONTRACT_ALL') {
			interval = 'daily';
			orderBy = 'dateData';
			fromDate = fromDate.format('YYYY-MM-DD');
			toDate = toDate.format('YYYY-MM-DD');
			API = `/${interval}/site/${siteId}/period/${fromDate}/${toDate}/order/${orderBy}/ASC`;
		}

		// Fetch data and set data into state
		GYAxios.get(API).then((res) => {
			const data = !res.data.error ? res.data : [];
			setSensorDataRes(data);
		});
	}, [selectedSite, sensorDataFilter, minDate]);

	useEffect(() => {
		getSensorData();
	}, [selectedSite, sensorDataFilter, getSensorData]);

	useEffect(() => {
		if (!selectedSite?.value?.power) { return }
		if (prevSensorDataRes.current === sensorDataRes) { return }
		prevSensorDataRes.current = sensorDataRes;

		let dataToPlot = sensorDataFilter.interval !== '1Y' ? {
			dates: [],
			solarON: [],
			solarOFF: [],
			solarTotal: [],
			gridTotal: [],
			irr: [],
			PR: [],
			// module_temp: [],
			ambient_temp: [],
			deemedOutput: [],
			consumption: []
		} : {
			dates: [],
			solarON: [],
			solarOFF: [],
			solarTotal: [],
			gridTotal: [],
			irr: [],
			PR: [],
			ambient_temp: [],
		};

		const interval = sensorDataFilter.interval;
		const range = sensorDataFilter.range;

		// only for contract years
		let buffer = sensorDataFilter.interval !== '1Y' ? {
			solarON: 0,
			solarOFF: 0,
			solarTotal: 0,
			gridTotal: 0,
			irr: 0,
			ambientTemp: 0,
			deemedOutput: 0,
			consumption: 0
			// PR: 0 // This will be calculated when all data for this buffer is collected
		} : {
			solarON: 0,
			solarOFF: 0,
			solarTotal: 0,
			gridTotal: 0,
			irr: 0,
			ambientTemp: 0,
		}
		let dateCursor = sensorDataFilter.moments[0];
		sensorDataRes.forEach((resRow, index) => {
			const siteCapacity = selectedSite.value.power;
			// Deemed Energy output
			const irradiance_sum = solarCalc.calculateIrr(resRow.irradiance_sum);
			const deo = solarCalc.DEOCalc(resRow.theoretical_PR, siteCapacity, irradiance_sum, resRow.m_GA_irr || 0, resRow.m_GA_prod || 0, resRow.m_TPA_irr || 0, resRow.m_TPA_prod || 0);
			
			// Check if we should set deemedOutput and consumption to 0 based on current date and 10th day rule
			const nextMonth10th = moment(resRow.dateData).add(1, 'month').set({ date: 10 }); // 10th of the next month from row date
			const currentDate = moment();
			const shouldHideZeroDEO = currentDate.isSameOrBefore(nextMonth10th);

			const deemedOutput = shouldHideZeroDEO ? 0 : (deo > resRow.solar_total ? deo : resRow.solar_total);
			const consumption = shouldHideZeroDEO ? 0 : parseFloat(solarCalc.consumptionCalc(resRow.solar_total, deemedOutput));

			if (range !== 'CONTRACT_ALL') {
				// brut PRs
				let brut_PR = /Y$/gi.test(interval) ? (resRow.solar_total / (siteCapacity * (resRow.irradiance_sum / 6000)) * 100) : resRow.brut_PR;
				if (brut_PR > 100) { brut_PR = 100 }

				// dates
				if (/M$/gi.test(interval)) { dataToPlot.dates.push(moment(resRow.dateData).format('MMM YYYY')) }
				else if (/Y$/gi.test(interval)) { dataToPlot.dates.push(resRow.yearData) }
				else { dataToPlot.dates.push(moment(resRow.dateData).format('MM/DD/YYYY')) }

				dataToPlot.ambient_temp.push(parseFormatNumber(resRow.avg_temp_ambient || resRow.ambient_temp_avg || 0, 2));
				dataToPlot.irr.push(parseFormatNumber(irradiance_sum, 2));
				dataToPlot.PR.push(parseFormatNumber(brut_PR, 2));
				dataToPlot.solarON.push(parseFormatNumber(resRow.solar_ON, 2));
				dataToPlot.solarTotal.push(parseFormatNumber(resRow.solar_total, 3));
				dataToPlot.solarOFF.push(parseFormatNumber(resRow.solar_OFF, 2));
				dataToPlot.gridTotal.push(parseFormatNumber(resRow.grid_total, 2));
				if (interval === '1M') dataToPlot.deemedOutput.push(parseFormatNumber(deemedOutput, 2));
				if (interval === '1M') dataToPlot.consumption.push(parseFormatNumber(consumption, 2));
			}
			if (range === 'CONTRACT_ALL') {
				const rowDate = moment(resRow.dateData);
				// put cumulative data in dataToPlot
				const accumulateDataToPlot = () => {
					buffer.ambientTemp = (buffer.ambientTemp + (resRow.ambient_temp_avg || 0)) / 2;
					buffer.irr += resRow.irradiance_sum;
					buffer.solarON += resRow.solar_ON;
					buffer.solarOFF += resRow.solar_OFF;
					buffer.solarTotal += resRow.solar_total;
					buffer.gridTotal += resRow.grid_total;
					if (interval === '1M') buffer.deemedOutput += parseFormatNumber(deemedOutput, 2);
					if (interval === '1M') buffer.consumption += parseFormatNumber(consumption, 2);
				};
				const pushDataToPlot = () => {
					dataToPlot.dates.push(`Year ${dataToPlot.dates.length + 1}`);
					dataToPlot.ambient_temp.push(parseFormatNumber(buffer.ambientTemp, 2));
					dataToPlot.irr.push(parseFormatNumber(buffer.irr / 6000, 2));
					dataToPlot.PR.push(parseFormatNumber(buffer.solarTotal / (siteCapacity * (buffer.irr / 6000)) * 100, 2));
					dataToPlot.solarON.push(parseFormatNumber(buffer.solarON, 2));
					dataToPlot.solarTotal.push(parseFormatNumber(buffer.solarTotal, 3));
					dataToPlot.solarOFF.push(parseFormatNumber(buffer.solarOFF, 2));
					dataToPlot.gridTotal.push(parseFormatNumber(buffer.gridTotal, 2));
					if (interval === '1M') dataToPlot.deemedOutput.push(parseFormatNumber(buffer.deemedOutput, 2));
					if (interval === '1M') dataToPlot.consumption.push(parseFormatNumber(buffer.consumption, 2));
					buffer = interval !== '1Y' ? {
						solarON: 0,
						solarOFF: 0,
						solarTotal: 0,
						gridTotal: 0,
						irr: 0,
						ambientTemp: 0,
						deemedOutput: 0,
						consumption: 0
					} : {
						solarON: 0,
						solarOFF: 0,
						solarTotal: 0,
						gridTotal: 0,
						irr: 0,
						ambientTemp: 0
					}
				}
				if (index >= sensorDataRes.length - 1) {
					accumulateDataToPlot();
					pushDataToPlot();
					return;
				}
				if (rowDate.valueOf() >= moment(dateCursor).add(1, 'year').valueOf()) {
					dateCursor = moment(rowDate);
					pushDataToPlot();
					accumulateDataToPlot();
					return;
				}
				accumulateDataToPlot();
			}
		});

		setSensorData(dataToPlot);
	}, [sensorDataFilter, sensorDataRes, selectedSite]);

	useEffect(() => {
		const colors2 = [
			"#ffc602",
			"#f28a10",
			"#12a8e0",
			"#86bc25",
			"#9543e0",
			"#c23531",
			"#385723",
			"#466BC4"
		];
		const colorVN = [
			"#ffc602",
			"#f28a10",
			"#12a8e0",
			"#86bc25",
			"#9543e0",
			"#c23531",
			"#25BCA7",
			"#790D9B"
		]
		if (sensorData != null) {
			sensorData.dates.forEach((item, i) => {
				if (item === currentMonth) {
					sensorData.deemedOutput[i] = 0;
					sensorData.consumption[i] = 0;
				}
			})
			const optionSensorData = {
				title: {
					text: null,
				},
				grid: {
					top: 120,
					right: "15%",
				},
				color: showDEO ? colorVN : colors2,
				tooltip: {
					trigger: "axis",
					axisPointer: {
						type: "shadow",
					},
					formatter: function (params) {
						var total = 0;
						var solar_total = 0;

						var solar_on_selected = false;
						var solar_off_selected = false;
						var solar_grid_selected = false;

						var colorON = "rgb(136,185,23, 0)";
						var colorOFF = "rgb(136,185,23, 0)";
						var colorGrid = "rgb(136,185,23, 0)";
						var coloredDot = (color) =>
							'<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:' +
							color +
							'"></span>';
						let htmlResult = "</br>";

						params.forEach((item, i) => {
							var html;
							if (item.seriesName === solarLabel[0] || item.seriesName === solarLabel[1]) {
								solar_total += +parseFloat(item.data).toFixed(2);
								switch (item.seriesName) {
									case solarLabel[0]:
										colorON = item.color;
										solar_on_selected = true;
										break;
									case solarLabel[1]:
										colorOFF = item.color;
										solar_off_selected = true;
										break;
									default:
										break;
								}
							}
							if (item.seriesName === "Grid consumption") {
								colorGrid = item.color;
								solar_grid_selected = true;
							}
							if (["Grid consumption", solarLabel[0], solarLabel[1]].includes(item.seriesName)) {
								total += +parseFloat(item.data).toFixed(2);
								html = `<p>${coloredDot(item.color)} ${params[i].axisValue} ${item.seriesName}: ${parseFloat(item.data).toFixed(2)} kWh</p>`;
							}
							if (item.seriesName === "Ambient Temp") {
								html = `<p>${coloredDot(item.color)} ${params[i].axisValue} ${item.seriesName}: ${parseFloat(item.data).toFixed(2)} °C</p>`;
							}
							if (item.seriesName === "Irradiation") {
								html = `<p>${coloredDot(item.color)} ${params[i].axisValue} ${item.seriesName}: ${parseFloat(item.data).toFixed(2)} kWh/m\xB2</p>`;
							}
							if (item.seriesName === "Performance Ratio") {
								html = `<p>${coloredDot(item.color)} ${params[i].axisValue} ${item.seriesName}: ${parseFloat(item.data).toFixed(2)} %</p>`;
							}
							if (item.seriesName === "Deemed Energy Output") {
								html = `<p>${coloredDot(item.color)} ${params[i].axisValue} ${item.seriesName}: ${parseFloat(item.data).toFixed(2)} kWh</p>`;
							}
							if (item.seriesName === "Consumption (%)") {
								html = `<p>${coloredDot(item.color)} ${params[i].axisValue} ${item.seriesName}: ${parseFloat(item.data).toFixed(2)} %</p>`;
							}
							htmlResult += html;
						});

						switch (true) {
							case solar_grid_selected:
								if (solar_on_selected && solar_off_selected) {
									htmlResult += "<hr style='padding:0; margin:10px 0'/>";
									htmlResult +=
										"<p>" +
										coloredDot(colorON) +
										coloredDot(colorOFF) +
										"Total solar production: <b>" +
										solar_total.toFixed(2) +
										" kWh</b></p>";
									htmlResult +=
										"<p>" +
										coloredDot(colorON) +
										coloredDot(colorOFF) +
										coloredDot(colorGrid) +
										"Total consumption: <b>" +
										total.toFixed(2) +
										" kWh</b></p>";
								} else if (solar_on_selected || solar_off_selected) {
									htmlResult += "<hr style='padding:0; margin:10px 0'/>";
									htmlResult +=
										"<p>" +
										coloredDot(colorON) +
										coloredDot(colorOFF) +
										coloredDot(colorGrid) +
										"Total selected consumption: <b>" +
										total.toFixed(2) +
										" kWh</b></p>";
								} else htmlResult += "<br/>";
								break;

							default:
								if (solar_on_selected && solar_off_selected) {
									htmlResult += "<hr style='padding:0; margin:10px 0'/>";
									htmlResult +=
										"<p>" +
										coloredDot(colorON) +
										coloredDot(colorOFF) +
										"Total solar production: <b>" +
										solar_total.toFixed(2) +
										" kWh</b></p>";
								} else htmlResult += "<br/>";
								break;
						}
						return htmlResult;
					},
				},
				legend: {
					data: showDEO ? [
						solarLabel[0],
						solarLabel[1],
						"Grid consumption",
						"Irradiation",
						"Ambient Temp",
						"Performance Ratio",
						"Deemed Energy Output",
						`Consumption (%)`
					] : [solarLabel[0],
					solarLabel[1],
						"Grid consumption",
						"Irradiation",
						// "Module Temp",
						"Ambient Temp",
						"Performance Ratio"],
					top: "7%",
					selected: {
						"Grid consumption": false,
						"Ambient Temp": false,
					},
				},
				toolbox: {
					show: true,
					left: "right",
					feature: {
						magicType: {
							show: true,
							title: {
								line: "",
								bar: "",
								stack: "",
								tiled: "",
							},
							type: ["bar", "tiled"],
							iconStyle: {
								textPosition: "top",
							},
						},
						restore: {
							show: true,
							title: " ",
						},
						saveAsImage: {
							show: true,
							title: " ",
						},
					},
				},
				xAxis: [
					{
						type: "category",
						axisTick: {
							show: false,
						},
						data: sensorData.dates,
					},
				],
				yAxis: [
					{
						type: "value",
						name: "Energy",
						// min: 0,
						// max: Math.ceil(Math.max(...sensorData.solarON, ...sensorData.solarOFF, ...sensorData.gridTotal, ...sensorData.deemedOutput) / 5000) * 5000,
						axisLabel: {
							formatter: "{value} kWh",
						},
						axisLine: { show: true },
						axisTick: {
							show: true,
						},
						splitLine: false,
					},
					{
						type: "value",
						name: "Irradiation",
						min: 0,
						max: Math.ceil(Math.max(...sensorData.irr)),
						axisLabel: {
							formatter: "{value} kWh/m\xB2",
						},
						axisLine: { show: true },
						axisTick: {
							show: true,
						},
						splitLine: false,
					},
					{
						type: "value",
						name: "Temp",
						min: 0,
						max: Math.ceil(Math.max(...sensorData.ambient_temp) / 5) * 5,
						axisLabel: {
							formatter: "{value} °C",
						},
						axisLine: { show: true },
						axisTick: {
							show: true,
						},
						splitLine: false,
						offset: 100,
					},
					{
						type: "value",
						name: "Percent",
						min: 0,
						max: 100,
						axisLabel: {
							formatter: "{value} %",
						},
						axisLine: { show: true },
						axisTick: {
							show: true,
						},
						splitLine: false,
						offset: 160,
					},
				],
				series: [
					{
						name: solarLabel[0],
						type: "bar",
						stack: "总量",
						barGap: 0,
						data: sensorData.solarON,
					},
					{
						name: solarLabel[1],
						type: "bar",
						stack: "总量",
						barGap: 0,
						data: sensorData.solarOFF,
					},
					{
						name: "Grid consumption",
						type: "bar",
						stack: "总量",
						data: sensorData.gridTotal,
					},
					{
						name: "Irradiation",
						type: "bar",
						yAxisIndex: 1,
						data: sensorData.irr,
					},
					{
						name: "Ambient Temp",
						type: "line",
						yAxisIndex: 2,
						data: sensorData.ambient_temp,
					},
					{
						name: "Performance Ratio",
						type: "bar",
						yAxisIndex: 3,
						data: sensorData.PR,
					},
					showDEO ?
						{
							name: "Deemed Energy Output",
							type: "bar",
							yAxisIndex: 0,
							data: sensorData.deemedOutput,
						} : null,
					showDEO ?
						{
							name: "Consumption (%)",
							type: "bar",
							yAxisIndex: 3,
							data: (sensorData.consumption),
						} : null
				],
			};

			setSensorDataGraphConfig(optionSensorData);
		}
	}, [sensorData, solarLabel, showDEO, currentMonth]);

	return (
		<div {...props} style={{ ...props.style, display: selectedChart !== toggleCharts.sensorChart ? 'none' : 'block' }}>
			<h3 style={{ marginBottom: '0.3em' }}>All Data</h3>
			<AnalyticChartDateOptions showRangeOption={true} defaultSelected={defaultSensorDataFilter} intervalOptions={sensorDataIntervalFilterOptions} rangeOptions={sensorDataRangeFilterOptions} otherOptions={dateRangeOtherOptions} controlledOtherOptionVal={dateRangeControlledOtherVal} onChange={handleSensorDataFilterChange} onOtherOptionsChange={handleOtherOptionsChange} minDate={minDate} showHelperText={showHelperText} helperText={helperText} />
			{(sensorDataGraphConfig) ? (
				<ReactEcharts
					style={{ height: "450px", padding: "0px 0px 20px 0px" }}
					option={sensorDataGraphConfig}
					notMerge={true}
					lazyUpdate={true}
				/>
			) : (
				<LoaderGraph style={{ height: "450px" }} />
			)}
		</div>
	)
}